import { getIssuerRoute, getParentRoute } from '@/creditflowresearch/Router/RouteWithRedirect/getNewRoute';
import URL from '@/creditflowresearch/Router/urlConstants';
import { toSlug } from '@framework/url/helpers/toSlug';
import {
  ISingleArticle,
  IArticleRelatedDeal,
  IArticleRelatedDealWithUrl,
  IArticleWithMappedData,
  ICompanyWithUrl,
  IGetArticleDealsResponse,
} from '@/creditflowresearch/domain/vo/ArticlesAndStats/Article';
import { IMarketTheme } from '@/creditflowresearch/domain/vo/ArticlesAndStats/IMarketTheme';
import { IMarketThemeGroup } from '@/creditflowresearch/domain/vo/Dictionary/MarketThemes';
import ICompany from '@/creditflowresearch/domain/vo/ICompany';
import { AbsProduct } from '@/creditflowresearch/domain/Product';
import {
  addSectorIndustryToData,
  mapSectorsIndustries,
} from '@/condor/application/Finsight/CreditFlowResearch/Articles/helpers';
import ISector from '@/creditflowresearch/domain/vo/IDealSector';
import IIndustry from '@/creditflowresearch/domain/vo/IIndustry';
import { defaultArticleRelatedDataValue } from '@/creditflowresearch/application/ArticlesAndStats/Article/constants';
import CreditFlowResearchRepository from '@/creditflowresearch/infrastructure/repository/CreditFlowResearchRepository';
import SectorRepository from '@/creditflowresearch/infrastructure/repository/SectorRepository';
import IndustryRepository from '@/creditflowresearch/infrastructure/repository/IndustryRepository';
import Container from '@framework/di/Container';
import { formatAttachment } from '@/creditflowresearch/application/ArticlesAndStats/helpers';
import DictionaryRepository from '@/creditflowresearch/infrastructure/repository/DictionaryRepository';
import { flattenThemes } from '@/condor/application/Finsight/CreditFlowResearch/Articles/helpers';

const getDealName = (deal: IArticleRelatedDeal): string => `${ deal.issuerName }-${ deal.seriesName }`;

const addUrlToDeal = (deal: IArticleRelatedDeal): IArticleRelatedDealWithUrl => ({
  ...deal,
  url: `${ URL.DEAL }-${ deal.viewId }-${ toSlug(getDealName(deal)) }`,
});

const addUrlToParent = (parent: ICompany): ICompanyWithUrl => ({
  ...parent,
  url: getParentRoute(parent),
});

const addUrlToIssuer = (issuer: ICompany): ICompanyWithUrl => ({
  ...issuer,
  url: getIssuerRoute(issuer),
});

export const getArticleWithData = ({
  article,
  themes,
  deals,
  parents,
  issuers,
  sectors,
  industries,
}: {
  article: ISingleArticle,
  themes: IMarketTheme[],
  deals: IGetArticleDealsResponse,
  parents: ICompany[],
  issuers: ICompany[],
  sectors: ISector[],
  industries: IIndustry[],
}): IArticleWithMappedData => {
  const processedArticle = {
    ...article,
    attachments: article.attachments.map(formatAttachment),
    themes,
    deals: deals.collection.map(addUrlToDeal),
    parents: parents.map(addUrlToParent),
    issuers: issuers.map(addUrlToIssuer),
    sectorOrIndustry: null,
    subsectorOrSubindustry: null,
  };
  const articleProductIds = Array.from(themes.reduce((acc, theme) => acc.add(theme.productId), new Set()));
  const isAbsOnlyArticle = articleProductIds.length === 1 && articleProductIds[0] === AbsProduct.PRODUCT_ID;
  const isCorporateArticle = !articleProductIds.includes(AbsProduct.PRODUCT_ID);
  const {
    sectorsIndustries,
    subsectorsSubindustries,
  } = mapSectorsIndustries(
    article,
    sectors,
    industries,
    isAbsOnlyArticle,
    isCorporateArticle,
  );
  return addSectorIndustryToData(processedArticle, sectorsIndustries, subsectorsSubindustries);
};

export const fetchArticleById = async (
  articleId: number,
  container: Container,
): Promise<IArticleWithMappedData> => {
  const articlesRepository = container.get<CreditFlowResearchRepository>(CreditFlowResearchRepository);
  const sectorsRepository = container.get<SectorRepository>(SectorRepository);
  const industriesRepository = container.get<IndustryRepository>(IndustryRepository);
  const dictionaryRepository = container.get<DictionaryRepository>(DictionaryRepository);

  const article = await articlesRepository.getArticleByViewId(Number(articleId));

  const [themeGroups, deals, parents, issuers, sectors, industries] = await Promise.all([
    dictionaryRepository.getThemes({ isGrouped: true }),

    article.deals.length > 0 ? articlesRepository.getArticleDeals({
      filters: { includedDealIds: article.deals },
    }) : defaultArticleRelatedDataValue,

    article.parents.length > 0 ? articlesRepository.getArticleCompanies(
      { companyIds: article.parents }) : [],

    article.issuers.length > 0 ? articlesRepository.getArticleCompanies(
      { companyIds: article.issuers }) : [],

    sectorsRepository.getList(),
    industriesRepository.getList(),
  ]);
  const themesList = flattenThemes(themeGroups as IMarketThemeGroup[]);
  const themes = article.themes.map((themeId) => themesList.find((t) => t.id === themeId));

  return getArticleWithData({
    article,
    themes,
    deals,
    parents,
    issuers,
    sectors,
    industries,
  });
};
